import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Loader } from "@/components/common/loader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { AlertsFeed } from "@/features/alerts/components/alerts-feed";
import { FIRM_FEED_LIMIT, listFirmCases } from "@/features/cases/api";
import { CaseStatusBadge } from "@/features/cases/components/case-status-badge";
import { type Case, isCaseDone } from "@/features/cases/types";
import { DashboardTranscriptAlerts } from "@/features/eservices/components/dashboard-transcript-alerts";
import { ESignatureSummaryFeed } from "@/features/esignatures/components/esignature-summary-feed";
import { DashboardOnboarding } from "@/features/portal/components/dashboard-onboarding";
import { cn } from "@/lib/utils";

/** "Sep 17" / "Today" / "—" for a case's next-due date, matching the mock. */
function formatDue(c: Case): string {
  if (isCaseDone(c.status) || c.nextDue == null) return "—";
  if (c.daysUntilDue === 0) return "Today";
  // Parse the date-only string via local parts so it never shifts a day in a
  // negative-offset timezone (see todays-priorities.tsx for the same guard).
  const [year, month, day] = c.nextDue.slice(0, 10).split("-").map(Number);
  if (year == null || month == null || day == null) return "—";
  return new Date(year, month - 1, day).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

function isOverdue(c: Case): boolean {
  return !isCaseDone(c.status) && c.daysUntilDue != null && c.daysUntilDue < 0;
}

function CasesFeed() {
  const [cases, setCases] = useState<Case[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    listFirmCases("open")
      .then((result) => {
        if (!active) return;
        setCases(result.cases);
        setTruncated(result.truncated);
      })
      .catch(() => active && toast.error("Couldn't load cases."))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  return (
    <div className="grid gap-3">
      {loading ? (
        <div className="flex justify-center py-10">
          <Loader />
        </div>
      ) : cases.length === 0 ? (
        <div className="bg-surface-hover flex min-h-[220px] flex-col items-center justify-center gap-3 rounded-lg px-6 py-8 text-center">
          <svg width="64" height="48" viewBox="0 0 64 48" fill="none" aria-hidden="true">
            <rect
              x="6"
              y="10"
              width="52"
              height="34"
              rx="6"
              stroke="#2457E6"
              strokeOpacity="0.35"
              strokeWidth="2"
            />
            <rect x="14" y="4" width="36" height="34" rx="6" fill="#FFFFFF" stroke="#2457E6" strokeWidth="2" />
            <path
              d="M24 16h16M24 23h12"
              stroke="#2457E6"
              strokeOpacity="0.5"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
          <span className="text-[15px] font-semibold">No open cases yet</span>
          <span className="text-muted-foreground max-w-sm text-[13px]">
            Create your first case to start tracking IRS correspondence, deadlines and tasks in one
            place.
          </span>
          <Button
            asChild
            variant="outline"
            className="border-info text-info hover:text-info mt-1 h-10 rounded-[11px] px-4 font-semibold"
          >
            <Link to="/clients">Create a case</Link>
          </Button>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow className="[&>th]:text-muted-foreground [&>th]:text-xs [&>th]:font-semibold [&>th]:tracking-wide [&>th]:uppercase">
              <TableHead>Client</TableHead>
              <TableHead>Resolution</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Due</TableHead>
              <TableHead>Steps</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {cases.map((c) => (
              <TableRow key={c.id} className="hover:bg-accent">
                <TableCell className="font-medium">
                  <Link
                    to={`/client/${c.client}/cases/${c.id}`}
                    className="hover:text-primary hover:underline"
                  >
                    {c.clientName}
                  </Link>
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {c.resolutionTypeDisplay}
                </TableCell>
                <TableCell>
                  <CaseStatusBadge status={c.status} label={c.statusDisplay} />
                </TableCell>
                <TableCell
                  className={cn(
                    "text-sm",
                    isOverdue(c) ? "text-destructive font-medium" : "text-muted-foreground",
                  )}
                >
                  {formatDue(c)}
                </TableCell>
                <TableCell>{c.openStepCount}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {truncated && (
        <p className="text-muted-foreground text-center text-xs">
          Showing the first {FIRM_FEED_LIMIT} cases — refine with a filter to see more.
        </p>
      )}
    </div>
  );
}

/**
 * Firm control-center panel (legacy `/home`): the Cases/Tasks feed wired to the
 * real API, plus the alert tabs that later tiers populate (transcript/other/
 * email alerts) and the Tier 5 seams (e-signatures, onboarding).
 */
// Segmented pill tabs (Calm Studio): the active tab is a WHITE pill in a muted
// container, matching the Control Center mockup. shadcn's active state defaults
// to `bg-background` (here the gray canvas), so force `bg-card` (white) and give
// inactive tabs the muted-foreground colour the mockup uses.
const TAB_CLASS =
  "h-[34px] flex-none rounded-[9px] px-3.5 text-[13px] font-medium text-muted-foreground data-[state=active]:bg-card data-[state=active]:font-semibold data-[state=active]:text-foreground";

export function ControlCenterCases() {
  return (
    <Card className="gap-0 overflow-hidden py-0">
      <Tabs defaultValue="cases">
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-x-4 gap-y-3 border-b px-6 py-5">
          <CardTitle className="font-heading text-xl">Control Center</CardTitle>
          <TabsList className="bg-muted h-auto gap-1 rounded-sm p-1">
            <TabsTrigger value="cases" className={TAB_CLASS}>
              Cases &amp; tasks
            </TabsTrigger>
            <TabsTrigger value="transcript" className={TAB_CLASS}>
              Transcripts
            </TabsTrigger>
            <TabsTrigger value="alerts" className={TAB_CLASS}>
              Alerts
            </TabsTrigger>
            <TabsTrigger value="esign" className={TAB_CLASS}>
              E-signatures
            </TabsTrigger>
            <TabsTrigger value="onboarding" className={TAB_CLASS}>
              Onboarding
            </TabsTrigger>
          </TabsList>
        </CardHeader>
        <CardContent className="px-6 py-6">
          <TabsContent value="cases">
            <CasesFeed />
          </TabsContent>
          <TabsContent value="transcript">
            <DashboardTranscriptAlerts />
          </TabsContent>
          <TabsContent value="alerts">
            <AlertsFeed />
          </TabsContent>
          <TabsContent value="esign">
            <ESignatureSummaryFeed />
          </TabsContent>
          <TabsContent value="onboarding">
            <DashboardOnboarding />
          </TabsContent>
        </CardContent>
      </Tabs>
    </Card>
  );
}
